import { toast } from "react-toastify";

import Axios from "axios";

import UserService from "Services/user";
import Analytics from "Services/analytics";
import I18nService from "Services/i18n";
import NotixLoggerService from "Services/logger";
import { READONLY_WHITE_LIST } from "../Constants/readonlyWhiteList";

class Api {
  constructor(baseUrl = `/api/client/`) {
    this.instance = Axios.create({
      baseURL: baseUrl,
      validateStatus: (status) => {
        return status >= 200 && status < 300;
      },
    });

    this.instance.interceptors.request.use((conf) => {
      const requestConfig = { ...conf };
      requestConfig.headers = {
        "X-SSP-LANGUAGE": I18nService.getDefaultLanguage(),
      };

      if (UserService && UserService.get()) {
        const user = UserService.get();
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `${user.tokenType} ${user.apiToken}`,
          "X-Google-Client-ID": Analytics.getGoogleAnalyticsClientId(),
        };

        const inWhiteList = READONLY_WHITE_LIST.map((w) =>
          requestConfig.url.includes(w)
        ).includes(true);

        if (
          UserService.isReadOnly() &&
          requestConfig.method !== "get" &&
          !inWhiteList
        ) {
          toast.warn("You are in read-only mode. Block request");
          const warnBody = {
            name: document.location.href,
            message: "You are in read-only mode. Block request",
          };
          NotixLoggerService.logError(warnBody);

          return {
            ...requestConfig,
            cancelToken: "read-only",
          };
        }
      }

      return requestConfig;
    });

    this.instance.interceptors.response.use(
      (response) => {
        switch (response.headers["content-type"]) {
          case "application/json":
          case "application/json; charset=utf-8":
          case "text/html; charset=UTF-8":
            return response.data;
          case "text/csv; charset=utf-8":
          case "application/pdf":
            return response;
          default:
            throw new Error("Unsupported content type");
        }
      },

      (error) => {
        if (!error.response) {
          return Promise.reject(error.response && error.response.data);
        }

        const { status, data, request } = error.response;
        const url = error.response.config?.url ?? null;

        if (url !== null && url !== "/errors/log") {
          const errorBody = {
            message: error.response.config.url,
            name: request?.responseURL,
            code: error.response.status.toString(),
            data: JSON.stringify(data),
            headers: JSON.stringify(error.response.headers),
            method: error.response.config.method,
            body: JSON.stringify(error.response.config.data), // check on always {}
            stack: "",
          };

          NotixLoggerService.logError(errorBody);
        }

        if (status === 400) {
          return Promise.reject(data);
        }

        if (status === 401) {
          toast.error(data);
          setTimeout(() => {
            UserService.clearUserData().then(() => {
              if (window.location.pathname !== "/signIn" && window.location.pathname !== "/") {
                window.location = "/"
              }
            });
          }, 5000)
        }

        if (status === 419) {
          let message = data && data.errors && data.errors[0];
          if (!message) {
            message = data;
          }

          const errorObject = {
            message,
            code: 1,
          };

          toast.error(errorObject.message);

          return Promise.reject(new Error(errorObject.message));
        }

        if (status === 403) {
          const errorObject = {
            message: data || "Access Denied",
            code: 1,
          };

          toast.error(errorObject.message);

          return Promise.reject(new Error(errorObject.message));
        }

        if (status === 408) {
          return Promise.reject({ data, status });
        }

        // Captcha custom error
        if (status === 418) {
          return Promise.reject({ data, status });
        }

        if (status === 500) {
          const errorObject = {
            message: `Uncaught error - ${status}`,
            code: 1,
          };

          toast.error(errorObject.message);

          return Promise.reject(new Error(errorObject.message));
        }

        return Promise.reject(error.response && error.response.data);
      }
    );

    return this.instance;
  }
}

export const ApiService = new Api();
export const NotixApiService = new Api(`/api/notix/`);
export const NotixApi = new Api(`/ssp/`);
